import Mousetrap from 'mousetrap';
import utils from '../utils.ts';

function clearColors(radioButtons) {
  radioButtons.buttons.forEach((button) => {
    button.HTMLElements.wrapper.style.backgroundColor = '';
  });
}

/**
 * @param {object} button
 * @param {string} button.text
 * @param {string} button.value
 * @param groupName
 * @param radioButtons
 * @param {string[]} button.hotKey
 * @returns {HTMLLabelElement}
 */
function createRadioButton(button, groupName, radioButtons) {
  const wrapper = document.createElement('span');
  wrapper.style.display = 'inline-block';
  wrapper.style.width = '100%';
  const label = document.createElement('label');
  label.style.display = 'inline-block';
  label.style.width = '100%';
  const input = document.createElement('input');
  input.setAttribute('type', 'radio');
  input.setAttribute('name', groupName);
  input.setAttribute('value', button.value);
  input.addEventListener('change', function radioButtonChangeListener() {
    if (radioButtons.options.backgroundColor) {
      clearColors(radioButtons);
      wrapper.style.backgroundColor = this.checked ? radioButtons.options.backgroundColor : '';
    }
    const oldValue = radioButtons.value;
    if (this.checked) {
      radioButtons._valueObj.value = button.value;
      if (oldValue !== radioButtons.value) radioButtons.changed();
    }
  });
  input.style.verticalAlign = 'middle';
  input.style.marginRight = '0.7em';

  if (button.hotKey) {
    Mousetrap.bind(button.hotKey, () => {
      input.click();
    });
  }

  label.appendChild(input);
  const span = document.createElement('span');
  span.innerHTML = button.text;
  label.appendChild(span);
  wrapper.appendChild(label);
  return {
    input,
    label,
    span,
    wrapper,
  };
}

export default class RadioButtons {
  /**
   * @param {HTMLDivElement} displayDiv
   * @param {object} options
   * @param {string} [options.groupName]
   * @param {string} [options.layout] ='vertical' default or 'horizontal'
   * @param {object[]} options.buttons
   * @param {string} options.buttons[].text visual descriptor
   * @param {string} options.buttons[].value value set on click
   * @param {string[]} options.buttons[].hotKey[] hotkeys to set each button.
   * @param {string} options.backgroundColor to optionally highlight the selected choice
   */
  constructor(displayDiv, options) {
    if (!(displayDiv instanceof window.HTMLElement))
      throw new Error('displayDiv must be an HTMLElement.');
    this.callbacks = [];
    this.displayDiv = displayDiv;
    this.options = utils.merge(
      {
        layout: 'vertical',
        groupName: `radioGroup-${utils.uuid}`,
      },
      options
    );

    this._valueObj = {};
    if (options.buttons) this.setButtons(options.buttons);
  }

  get value() {
    return this._valueObj.value;
  }

  reset() {
    this._valueObj = {};
    this.buttons.forEach((button) => {
      button.HTMLElements.input.checked = false;
    });
    clearColors(this);
  }

  /**
   * @param {object[]} buttons
   * @param {string} buttons.text
   * @param {string} buttons.value
   * @param {string[]} buttons.hotKey
   */
  setButtons(buttons) {
    this.displayDiv.style.padding = '10px';

    if (this.options.layout === 'horizontal') {
      this.displayDiv.style.display = 'flex';
      this.displayDiv.style.flexDirection = 'row';
      this.displayDiv.style.flexWrap = 'wrap';
      this.displayDiv.style.justifyContent = 'space-around';
    }

    const docFrag = document.createDocumentFragment();
    this.buttons = utils.clone(buttons).map((button, index, arr) => {
      button.HTMLElements = createRadioButton(button, this.options.groupName, this);
      docFrag.appendChild(button.HTMLElements.wrapper);
      if (index !== arr.length - 1 && this.options.layout !== 'horizontal') {
        docFrag.appendChild(document.createElement('br'));
      }
      return button;
    });
    this.displayDiv.appendChild(docFrag);
  }

  /**
   * @param {Function} callback
   * @param {object|Function} [thisArg]
   */
  addListener(callback, thisArg) {
    callback = thisArg ? callback.bind(thisArg) : callback;
    if (!utils.isFunction(callback)) throw new Error('Callback must be a function.');
    this.callbacks.push(callback);
  }

  /**
   * changed -- notify all listeners our value has changed.
   */
  changed() {
    this.callbacks.forEach((cb) => cb(this.value));
  }
}
